import { Component, type ReactNode } from 'react';
import { setLowDataMode } from '../viewer/lowDataMode';

/**
 * Wraps the 3D viewer with a uniform "Cesium isn't going to work — fall
 * back to 2D" path. Three failure modes are caught:
 *
 *   1. The lazy-imported chunk never resolves (e.g. launching the PWA
 *      offline before Cesium has ever been cached). Surfaces via React's
 *      error boundary contract → componentDidCatch.
 *   2. WebGL context loss / unavailable. Cesium itself emits a
 *      'manglillo:cesium-failed' window event from CesiumViewer when
 *      the canvas loses context or tiles never load.
 *   3. Anything CesiumViewer's setup code rejects.
 *
 * On any of those we persist low-data preference, drop camera params
 * from the URL, and reload — same path the boot-screen "Low Data"
 * button takes.
 */
export class CesiumChunkBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  private onCesiumFailed = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    this.fallbackTo2D(detail ?? 'cesium-failed-event');
  };

  componentDidMount() {
    window.addEventListener('manglillo:cesium-failed', this.onCesiumFailed);
  }

  componentWillUnmount() {
    window.removeEventListener('manglillo:cesium-failed', this.onCesiumFailed);
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    this.fallbackTo2D(error);
  }

  private fallbackTo2D(reason: unknown) {
    console.warn('[Manglillo] Cesium unavailable — falling back to 2D.', reason);
    setLowDataMode(true);
    // Strip ?mode=3d (and any shared-view camera params) so the inline
    // boot script doesn't bounce us right back into 3D on reload.
    const url = new URL(window.location.href);
    const lot = url.searchParams.get('lot');
    url.search = '';
    if (lot) url.searchParams.set('lot', lot);
    window.location.replace(url.toString());
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
